import React, { useState } from "react";
import { Card, Button, Typography, Row, Col, Grid, Tag } from "antd";
import {
  ArrowsAltOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  HomeOutlined,
  RestOutlined,
  CarOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import InstantCallModal from "./InstantCallModal";
import { firebaseService } from "../../lib/services/firebaseService";
import { Property } from "../../types/propert";

const { Text } = Typography;
const { useBreakpoint } = Grid;

const PRIMARY_DARK = "#011F3A";
const PRICE_BACKGROUND_GRAY = "#E0E0E0";
const LIGHT_BLUE_ACCENT = "#2196F3";
const MAROON = "#3a0000ff";

// Market Status Colors (still used for logic if you want later)
const MARKET_STATUS_COLORS = {
  sold: "#16A34A",
  open: "#2196F3",
  "in-progress": "#F59E0B",
};

const StatusLabel = ({ text, color = PRIMARY_DARK, style = {} }) => (
  <Text
    style={{
      fontSize: "11px",
      fontWeight: 700,
      textTransform: "uppercase",
      color: color,
      ...style,
    }}
  >
    {text}
  </Text>
);

// Kept in case you still want it somewhere else
const MarketStatusBadge = ({ status = "open" }) => {
  const getStatusText = () => {
    switch (status) {
      case "sold":
        return "Sold";
      case "available":
        return "Available";
      case "in-progress":
        return "In Progress";
      default:
        return "available";
    }
  };

  return (
    <Tag
      color={MARKET_STATUS_COLORS[status] || MARKET_STATUS_COLORS.open}
      style={{
        margin: 0,
        fontWeight: 600,
        fontSize: "10px",
        textTransform: "uppercase",
        border: "none",
        color: "#fff",
        padding: "2px 8px",
      }}
    >
      {getStatusText()}
    </Tag>
  );
};

const PropertyCard = ({ property }) => {
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const [instantCallModalVisible, setInstantCallModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  const defaultProperty = {
    id: 1,
    propertyType: "Residential",
    finalPrice: "4.2M",
    currency: "AED",
    ownershipStatus: "Freehold",
    exclusivity: true, 
    city: "Dubai",
    title: "Property Title",
    description: "", 
    plotSize: 2400,
    areaUnit: "sq.ft",
    bedrooms: 0,
    bathrooms: 0,
    kitchens: 0,
    livingRooms: 0,
    parkingSpaces: 0,
    builtUpArea: 0,
    marketStatus: "open",
    listingType: "for_sale",
    furnishing: "Non-Furnished",
    statusReason: "",
    adminName: "",
    views: 0,
    viewHistory: [],
    lastViewedAt: null,
  };

  const prop = property || defaultProperty;

  // Function to increment views when user goes to detail page
  const handleViewDetails = async () => {
    if (prop.id) {
      try {
        setLoading(true);

        const propertyInstance =
          prop instanceof Property ? prop : new Property(prop);
        const updatedProperty = propertyInstance.copyWith({
          views: propertyInstance.views + 1,
          lastViewedAt: new Date(),
          updatedAt: new Date(),
        });

        firebaseService
          .addOrUpdateProperty(updatedProperty)
          .then(() => {
            console.log(
              `View count updated for property ${prop.id}. New count: ${updatedProperty.views}`
            );
          })
          .catch((error) => {
            console.error("Error updating view count:", error);
          });
      } catch (error) {
        console.error("Error in view increment:", error);
      } finally {
        navigate(`/property-detail/${prop.id}`);
        setLoading(false);
      }
    }
  };

  // Get view count for display
  const getViewCount = () => {
    return prop.views || 0;
  };

  const handleInstantCall = () => {
    setInstantCallModalVisible(true);
  };

  const handleCloseInstantCallModal = () => {
    setInstantCallModalVisible(false);
  };

  // Price logic (unchanged from your version)
  const hasPrice = prop.finalPrice > 0 || prop.listPrice > 0;
  const priceValue =
    prop.finalPrice && prop.finalPrice > 0
      ? prop.finalPrice
      : prop.listPrice || "";
  const price = hasPrice
    ? `${prop.currency || "AED"} ${priceValue}`
    : "Details on Request";

  // Header title based on listing type
  const getHeaderTitle = () => {
    const propertyType = prop.propertyType || "Property";
    const listingType = prop.listingType || "for_sale";

    if (listingType === "for_rent") {
      return `${propertyType.toUpperCase()} FOR RENT`;
    }
    return `${propertyType.toUpperCase()} FOR SALE`;
  };

  const headerTitle = getHeaderTitle();

  const cardWidth = screens.xs ? "100%" : 320;

  const isSold = prop.marketStatus === "sold";
  const isInProgress = prop.marketStatus === "in-progress";

  const displayTitle = prop.title || prop.statusReason || "Premium Property"; 

  const descriptionText =
    prop.adminName || "Property description not available.";

  const viewCount = getViewCount();

  const formattedArea = () => {
    const sizeNumber =
      typeof prop.plotSize === "number"
        ? prop.plotSize
        : typeof prop.builtUpArea === "number"
        ? prop.builtUpArea
        : null;

    if (sizeNumber != null) {
      return `${sizeNumber.toLocaleString()} ${prop.areaUnit || "sq.ft"}`;
    }
    return prop.areaUnit || "";
  };

  return (
    <>
      <Card
        style={{
          width: cardWidth,
          backgroundColor: "white",
          borderRadius: 8,
          boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
          overflow: "hidden",
          border: "none",
          display: "flex",
          flexDirection: "column",
          opacity: isSold ? 0.7 : 1,
          position: "relative",
        }}
        styles={{
          body: {
            padding: 0,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* 1. Top maroon header with view count */}
        <div
          style={{
            backgroundColor: MAROON,
            padding: screens.xs ? "8px 12px" : "10px 16px",
            position: "relative",
          }}
        >
          <Text
            style={{
              color: "#fff",
              fontWeight: 600,
              fontSize: screens.xs ? 14 : 15,
              textTransform: "uppercase",
            }}
          >
            {headerTitle}
          </Text>

          {/* View count circle on right - with increased padding */}
          <div
            style={{
              position: "absolute",
              right: screens.xs ? 10 : 16,
              top: "50%",
              transform: "translateY(-50%)",
              width: 32, 
              height: 32, 
              borderRadius: "50%",
              backgroundColor: "rgba(255,255,255,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 3,
              padding: "10px", // Added padding
            }}
          >
            <EyeOutlined
              style={{ fontSize: 12, color: "#fff" }} 
            />
            <Text
              style={{
                color: "#fff",
                fontSize: 11,
                fontWeight: 600,
                lineHeight: 1,
                marginLeft: 1, 
              }}
            >
              {viewCount}
            </Text>
          </div>
        </div>

        {/* 2. Tag row - Reorganized in columns */}
        <div
          style={{
            padding: screens.xs ? "10px 12px 4px" : "12px 16px 6px",
            backgroundColor: "#fff",
          }}
        >
          <Row justify="space-between" align="middle">
            {/* Left Column: Premium and City badges stacked */}
            <Col>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {/* Premium badge */}
                <Tag
                  color={LIGHT_BLUE_ACCENT}
                  style={{
                    borderRadius: 4,
                    margin: 0,
                    fontSize: 10,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    color: "#fff",
                    border: "none",
                    width: "fit-content",
                  }}
                >
                  Premium
                </Tag>
                {/* City badge */}
                {prop.city && (
                  <Tag
                    color={LIGHT_BLUE_ACCENT}
                    style={{
                      borderRadius: 4,
                      margin: 0,
                      fontSize: 10,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      color: "#fff",
                      border: "none",
                      width: "fit-content",
                    }}
                  >
                    {prop.city}
                  </Tag>
                )}
              </div>
            </Col>

            {/* Right Column: Freehold and Direct Seller stacked */}
            <Col style={{ textAlign: "right" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {prop.ownershipStatus && (
                  <StatusLabel
                    text={prop.ownershipStatus}
                    color={LIGHT_BLUE_ACCENT}
                    style={{ display: "block" }}
                  />
                )}
                <Text
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    color: "#000",
                  }}
                >
                  Direct Seller
                </Text>
              </div>
            </Col>
          </Row>
        </div>

        {/* 3. Description section with 1-line limit */}
        <div
          style={{
            padding: screens.xs ? "8px 12px 8px" : "10px 16px 10px",
            backgroundColor: "#fff",
            minHeight: screens.xs ? "32px" : "36px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Text
            style={{
              fontSize: screens.xs ? 13 : 14,
              fontWeight: 500,
              color: PRIMARY_DARK,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              lineHeight: "1.4",
              margin: 0,
              minHeight: screens.xs ? "2.8em" : "2.8em",
              height: screens.xs ? "2.8em" : "2.8em", 
              textAlign: "left",
            }}
          >
            {descriptionText}
          </Text>
        </div>

        {/* 4. Icons row (bed / bath / car / area) */}
        <div
          style={{
            backgroundColor: "#fff",
            padding: screens.xs ? "8px 8px 4px" : "10px 10px 6px",
          }}
        >
          <Row
            justify="space-between"
            align="middle"
            style={{
              borderRadius: 8,
              border: "1px solid #f0f0f0",
              padding: screens.xs ? "6px 10px" : "8px 12px",
            }}
          >
            <Col>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  minWidth: 50,
                }}
              >
                <HomeOutlined style={{ fontSize: 16, color: PRIMARY_DARK }} />
              </div>
            </Col>
            <Col>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  minWidth: 50,
                }}
              >
                <RestOutlined style={{ fontSize: 16, color: PRIMARY_DARK }} />
              </div>
            </Col>
            <Col>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  minWidth: 50,
                }}
              >
                <CarOutlined style={{ fontSize: 16, color: PRIMARY_DARK }} />
              </div>
            </Col>
            <Col>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  minWidth: 70,
                }}
              >
                <ArrowsAltOutlined
                  style={{ fontSize: 16, color: PRIMARY_DARK }}
                />
                <Text style={{ fontSize: 12, color: "#333" }}>
                  {formattedArea()}
                </Text>
              </div>
            </Col>
          </Row>
        </div>

        {/* 5. Price row: PRICE + grey pill */}
        <div
          style={{
            padding: screens.xs ? "10px 12px" : "12px 16px",
          }}
        >
          {isSold && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                backgroundColor: "rgba(0,0,0,0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
              }}
            >
              <Text
                style={{
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: screens.xs ? 18 : 20,
                  textTransform: "uppercase",
                }}
              >
                SOLD
              </Text>
            </div>
          )}

          <Row justify="space-between" align="middle">
            <Col>
              <Text
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#555",
                  textTransform: "uppercase",
                }}
              >
                Price
              </Text>
            </Col>
            <Col>
              <div
                style={{
                  backgroundColor: PRICE_BACKGROUND_GRAY,
                  padding: screens.xs ? "6px 12px" : "6px 16px",
                  borderRadius: 4,
                  minWidth: 140,
                  textAlign: "center",
                }}
              >
                <Text
                  strong
                  style={{
                    fontSize: screens.xs ? 13 : 14,
                    color: PRIMARY_DARK,
                    fontStyle: hasPrice ? "normal" : "italic",
                  }}
                >
                  {price}
                </Text>
              </div>
            </Col>
          </Row>
        </div>

        {/* 6. Grey separator */}
        <div
          style={{
            borderBottom: `1px solid ${PRICE_BACKGROUND_GRAY}`,
            margin: screens.xs ? "0 12px" : "0 16px",
            flexShrink: 0,
          }}
        />

        {/* 7. Bottom buttons with spacing */}
        <div
          style={{
            display: "flex",
            width: "100%",
            backgroundColor: "#fff",
            padding: screens.xs ? "12px" : "16px",
            gap: screens.xs ? "8px" : "12px", // Added gap between buttons
          }}
        >
          <Button
            onClick={handleViewDetails}
            disabled={isSold || loading}
            loading={loading}
            style={{
              flex: 1,
              borderRadius: 6, // Added border radius
              backgroundColor: isSold ? "#ccc" : MAROON,
              color: "#fff",
              border: "none",
              height: 40,
              fontSize: 13,
              fontWeight: 500,
              cursor: isSold ? "not-allowed" : "pointer",
            }}
          >
            {isSold ? "Sold Out" : loading ? "Loading..." : "View Details"}
          </Button>
          <Button
            onClick={handleInstantCall}
            disabled={isSold}
            style={{
              flex: 1,
              borderRadius: 6, // Added border radius
              backgroundColor: isSold ? "#ccc" : MAROON,
              color: "#fff",
              border: "none",
              height: 40,
              fontSize: 13,
              fontWeight: 500,
              cursor: isSold ? "not-allowed" : "pointer",
            }}
          >
            {isSold ? "Unavailable" : "Instant Call"}
          </Button>
        </div>
      </Card>

      {/* Instant Call Modal */}
      <InstantCallModal
        visible={instantCallModalVisible}
        onClose={handleCloseInstantCallModal}
        propertyId={prop.id}
      />
    </>
  );
};

export default PropertyCard;
